import {
  faBlockBrick,
  faPaintbrush,
  faPersonRunning,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faAlignLeft,
  faFileArchive,
  faFolder,
  faImages,
  faPalette,
} from "@fortawesome/pro-solid-svg-icons";
import { ReactNode } from "react";

import {
  Asset,
  Directory,
  File,
  ReferenceSet,
  Style,
} from "@/components/graphql/schema.graphql";
import { Icon } from "@/components/ui-v2";
import {
  AudioIcon,
  CanvasIcon,
  RealtimeIcon,
  ReframeIcon,
  RemoveBackgroundIcon,
  SafetensorsIcon,
  ThreeDIcon,
  TwoDIcon,
  UpscaleImageIcon,
  UpscaleVideoIcon,
  VectorizeIcon,
  VideoIcon,
} from "@/components/ui-v2/icons";
import { InferenceParameters, ObjectType } from "@/types";

import {
  is3DMimetype,
  isArchiveMimetype,
  isAudioMimetype,
  isImageMimetype,
  isPhotoshopMimeType,
  isVideoMimetype,
} from "./mimetype";

const aiModelExtensionsRegex = /\.(ckpt|safetensors)$/i;
const textExtensionsRegex = /\.(txt|toml|log)$/i;

export const fileIsModel = (file: Pick<File, "name">) => {
  return file.name?.match(aiModelExtensionsRegex);
};

const fileIsText = (file: Pick<File, "name">) => {
  return file.name?.match(textExtensionsRegex);
};

export const getObjectType = (
  obj:
    | Pick<File, "name" | "contentType" | "__typename">
    | Pick<Asset, "__typename">
    | Pick<Style, "__typename">
    | Pick<Directory, "__typename">
    | Pick<ReferenceSet, "__typename">,
): ObjectType => {
  switch (obj.__typename) {
    case "Asset":
      return "canvas";
    case "Style":
    case "ReferenceSet":
      return "style";
    case "Directory":
      return "directory";
    case "File":
      if (obj.contentType) {
        if (isPhotoshopMimeType(obj.contentType)) {
          return "psd";
        }
        if (isImageMimetype(obj.contentType)) {
          return "image";
        }
        if (isVideoMimetype(obj.contentType)) {
          return "video";
        }
        if (is3DMimetype(obj.contentType)) {
          return "mesh";
        }
        if (isAudioMimetype(obj.contentType)) {
          return "audio";
        }
        if (isArchiveMimetype(obj.contentType)) {
          return "archive";
        }
      }
      if (fileIsModel(obj)) {
        return "safetensors";
      }
      if (fileIsText(obj)) {
        return "text";
      }
      break;
  }
  return "unknown";
};

export const getObjectMetadata = (
  objectType?: ObjectType,
  inferenceParameters?: InferenceParameters,
) => {
  let label: string;
  let icon: ReactNode;

  if (!objectType) {
    return {
      icon: <TwoDIcon />,
      label: "Asset",
    };
  }

  switch (objectType) {
    case "image":
      if (!inferenceParameters) {
        icon = <TwoDIcon />;
        label = "Image";
      } else if (inferenceParameters?.realtime) {
        icon = <RealtimeIcon />;
        label = "Realtime Creation";
      } else if (inferenceParameters?.upscaleRatio) {
        icon = <UpscaleImageIcon />;
        label = "Image Upscale";
      } else if (inferenceParameters?.removeBackground) {
        icon = <RemoveBackgroundIcon />;
        label = "Remove BG";
      } else if (inferenceParameters?.reframe) {
        icon = <ReframeIcon />;
        label = "Reframe";
      } else if (inferenceParameters?.vectorize) {
        icon = <VectorizeIcon />;
        label = "Vectorize Image";
      } else {
        icon = <TwoDIcon />;
        label = "2D Forge";
      }
      break;
    case "video":
      if (!inferenceParameters) {
        icon = <VideoIcon />;
        label = "Video";
      } else if (inferenceParameters?.upscaleRatio) {
        icon = <UpscaleVideoIcon />;
        label = "Video Upscale";
      } else if (inferenceParameters?.removeBackground) {
        icon = <RemoveBackgroundIcon />;
        label = "Remove BG";
      } else if (inferenceParameters?.reframe) {
        icon = <ReframeIcon />;
        label = "Reframe";
      } else if (inferenceParameters?.addSfxToVideo) {
        icon = <AudioIcon />;
        label = "Add Sound Effect";
      } else {
        icon = <VideoIcon />;
        label = "Video Forge";
      }
      break;
    case "mesh":
      if (!inferenceParameters) {
        icon = <ThreeDIcon />;
        label = "3D Asset";
      } else if (inferenceParameters.retexture) {
        icon = <Icon icon={faPaintbrush} />;
        label = "Retexture";
      } else if (inferenceParameters.remesh) {
        icon = <Icon icon={faBlockBrick} />;
        label = "Remesh";
      } else if (inferenceParameters.rig) {
        icon = <Icon icon={faPersonRunning} />;
        label = "Rig";
      } else {
        icon = <ThreeDIcon />;
        label = "3D Forge";
      }
      break;
    case "audio":
      icon = <AudioIcon />;
      label = "Audio Asset";
      break;
    case "text":
      icon = <Icon className="fa-fw" icon={faAlignLeft} />;
      label = "Text";
      break;
    case "directory":
      icon = <Icon className="fa-fw" icon={faFolder} />;
      label = "Directory";
      break;
    case "archive":
      icon = <Icon className="fa-fw" icon={faFileArchive} />;
      label = "Archive";
      break;
    case "safetensors":
      icon = <SafetensorsIcon />;
      label = "Model";
      break;
    case "psd":
      icon = <Icon className="fa-fw" icon={faImages} />;
      label = "PSD";
      break;
    case "style":
      icon = <Icon icon={faPalette} />;
      label = "Style";
      break;
    case "canvas":
      icon = <CanvasIcon />;
      label = "Canvas";
      break;
    default:
      icon = <TwoDIcon />;
      label = "Image Asset";
  }

  return {
    icon,
    label,
  };
};
